import { useState } from 'react';
import { useCreateModel, useAddFile, useAddLink, usePreviewLinkMetadata, useDeleteModel } from '../hooks';
import { MultiFileUpload } from '@/components/MultiFileUpload';
import { Accordion } from '@/components/Accordion';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Button } from '@/components/Button';
import { useToast } from '@/components/ToastProvider';
import { useStorageStats } from '@/hooks/useStorageStats';
import {
  Link as LinkIcon,
  X,
  Plus,
  File,
  Check,
  Image as ImageIcon,
  User,
  Loader2,
} from 'lucide-react';
import type { ExternalModelMetadata } from '@maker-toolchest/shared';
import { createModelSchema } from '@maker-toolchest/shared';
import { zodErrorsToFieldErrors } from '@/utils/formErrors';

interface ModelUploadFormProps {
  onSuccess?: (modelId: string) => void;
}

interface PendingFile {
  id: string;
  file: File;
}

interface PendingLink {
  id: string;
  url: string;
  metadata?: ExternalModelMetadata;
  metadataError?: string;
  loading?: boolean;
}

export function ModelUploadForm({ onSuccess }: ModelUploadFormProps) {
  // Model form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [notes, setNotes] = useState('');

  // Pending files and links
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [pendingLinks, setPendingLinks] = useState<PendingLink[]>([]);

  // Add link form
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();
  const createMutation = useCreateModel();
  const addFileMutation = useAddFile();
  const addLinkMutation = useAddLink();
  const previewMetadataMutation = usePreviewLinkMetadata();
  const deleteMutation = useDeleteModel();
  const { data: storageStats } = useStorageStats();

  // Add multiple files from drag and drop
  const handleMultipleFilesSelect = (files: File[]) => {
    if (files.length === 0) return;

    // Check storage limits
    if (storageStats && storageStats.limitBytes !== Infinity) {
      // Calculate size of new files
      const newFilesSize = files.reduce((sum, file) => sum + file.size, 0);

      // Calculate size of already pending files
      const pendingFilesSize = pendingFiles.reduce((sum, pf) => sum + pf.file.size, 0);

      // Check if adding these files would exceed storage limit
      const totalAfterUpload = storageStats.usedBytes + pendingFilesSize + newFilesSize;

      if (totalAfterUpload > storageStats.limitBytes) {
        const limitMB = (storageStats.limitBytes / (1024 * 1024)).toFixed(0);
        toast.error(
          `Not enough storage space. You need to delete existing files or upgrade your plan to add these file(s). Storage limit: ${limitMB} MB.`
        );
        return;
      }
    }

    const newFiles = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));

    setPendingFiles((prev) => [...prev, ...newFiles]);

    // Auto-generate name from first file if name is empty
    if (!name.trim() && files.length > 0) {
      const generatedName = generateNameFromFilename(files[0].name);
      setName(generatedName);
      // Clear any name errors since we now have a name
      if (errors.name) {
        const { name, ...rest } = errors;
        setErrors(rest);
      }
    }

    const message =
      files.length === 1
        ? `${files[0].name} added to queue`
        : `${files.length} files added to queue`;
    toast.success(message);
  };

  // Remove file from pending
  const handleRemoveFile = (id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Add link with metadata extraction
  const handleAddLink = async () => {
    if (!linkUrl.trim()) return;

    try {
      new URL(linkUrl);
    } catch {
      setLinkError('Must be a valid URL');
      return;
    }

    const linkId = crypto.randomUUID();
    const newLink: PendingLink = {
      id: linkId,
      url: linkUrl.trim(),
      loading: true,
    };

    setPendingLinks((prev) => [...prev, newLink]);
    setLinkUrl('');
    setLinkError('');

    // Extract metadata
    try {
      const metadata = await previewMetadataMutation.mutateAsync({ url: newLink.url });
      setPendingLinks((prev) =>
        prev.map((link) =>
          link.id === linkId ? { ...link, metadata, loading: false } : link
        )
      );
    } catch (error: any) {
      setPendingLinks((prev) =>
        prev.map((link) =>
          link.id === linkId
            ? { ...link, metadataError: error.message, loading: false }
            : link
        )
      );
    }
  };

  // Remove link from pending
  const handleRemoveLink = (id: string) => {
    setPendingLinks((prev) => prev.filter((l) => l.id !== id));
  };

  // Use metadata from a link
  const useTitle = (title: string) => {
    setName(title);
    if (errors.name) {
      const { name, ...rest } = errors;
      setErrors(rest);
    }
  };

  const useDescription = (desc: string) => {
    setDescription(desc);
  };

  const useThumbnail = (url: string) => {
    setThumbnailUrl(url);
  };

  // Submit entire form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate model data with Zod
    const modelData = {
      name: name.trim(),
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    const result = createModelSchema.safeParse(modelData);

    if (!result.success) {
      const validationErrors = zodErrorsToFieldErrors(result.error);
      setErrors(validationErrors);
      return;
    }

    if (pendingFiles.length === 0 && pendingLinks.length === 0) {
      toast.error('Add at least one file or link');
      return;
    }

    setIsSubmitting(true);
    let createdModelId: string | null = null;

    try {
      // Step 1: Create the model
      const model = await createMutation.mutateAsync(modelData);
      createdModelId = model.id;

      // Step 2: Upload all files
      for (const pendingFile of pendingFiles) {
        await addFileMutation.mutateAsync({
          modelId: model.id,
          file: pendingFile.file,
          input: {},
        });
      }

      // Step 3: Add all links
      for (const pendingLink of pendingLinks) {
        await addLinkMutation.mutateAsync({
          modelId: model.id,
          input: {
            url: pendingLink.url,
          },
        });
      }

      toast.success('Model created successfully!');

      // Reset form
      setName('');
      setDescription('');
      setThumbnailUrl('');
      setNotes('');
      setPendingFiles([]);
      setPendingLinks([]);
      setErrors({});

      onSuccess?.(model.id);
    } catch (error: any) {
      // If model was created, roll back by deleting it
      // This will cascade delete files and remove them from storage
      if (createdModelId) {
        try {
          await deleteMutation.mutateAsync(createdModelId);
        } catch (deleteError: any) {
          console.error('Failed to rollback model creation:', deleteError);
        }
      }

      // Show error to user
      toast.error(error.message || 'Failed to create model');
      // Keep form open with data so user can fix and retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Generate a model name from a filename
  const generateNameFromFilename = (filename: string): string => {
    // Remove extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    // Replace - and _ with spaces, but only when not adjacent to spaces
    // e.g., "some-thing" -> "some thing", but "some - thing" stays as-is
    const nameWithSpaces = nameWithoutExt.replace(/(?<! )[-_](?! )/g, ' ');
    return nameWithSpaces;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name and Description at the top */}
      <div className="space-y-4">
        {/* Thumbnail Preview */}
        {thumbnailUrl && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Thumbnail Preview
            </label>
            <div className="relative inline-block">
              <img
                src={thumbnailUrl}
                alt="Model thumbnail"
                className="w-32 h-32 object-cover rounded border border-border"
              />
              <button
                type="button"
                onClick={() => setThumbnailUrl('')}
                className="absolute top-1 right-1 p-1 bg-background/80 hover:bg-background rounded-full"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        <Input
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) {
              const { name, ...rest } = errors;
              setErrors(rest);
            }
          }}
          error={errors.name}
          disabled={isSubmitting}
          required
          placeholder="e.g., Benchy, Calibration Cube"
        />

        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSubmitting}
          rows={3}
          placeholder="Optional description of the model"
        />
      </div>

      {/* Links Section */}
      <Accordion
        title="Links"
        defaultOpen={true}
        badge={pendingLinks.length || undefined}
        headerAction={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={(e) => {
              e.stopPropagation();
              // Focus link input
            }}
            disabled={isSubmitting}
          >
            Add Link
          </Button>
        }
      >
        <div className="space-y-3">
          {/* Add Link Form */}
          <div className="space-y-2">
            <p className="text-xs text-foreground-muted">
              Link to a page containing information about this model.
            </p>
            <div className="flex gap-2">
              <Input
                type="url"
                value={linkUrl}
                onChange={(e) => {
                  setLinkUrl(e.target.value);
                  setLinkError('');
                }}
                error={linkError}
                placeholder="https://..."
                disabled={isSubmitting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddLink();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleAddLink}
                disabled={!linkUrl.trim() || isSubmitting}
              >
                Add
              </Button>
            </div>
          </div>

          {/* Pending Links List */}
          {pendingLinks.length > 0 && (
            <div className="space-y-2">
              {pendingLinks.map((link) => (
                <div
                  key={link.id}
                  className="p-3 bg-background-subtle rounded-lg border border-border space-y-2"
                >
                  {/* Link header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <LinkIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-foreground break-all">{link.url}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveLink(link.id)}
                      className="text-foreground-muted hover:text-error"
                      disabled={isSubmitting}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Metadata loading/error/success */}
                  {link.loading && (
                    <div className="flex items-center gap-2 text-sm text-foreground-muted">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Extracting metadata...</span>
                    </div>
                  )}

                  {link.metadataError && (
                    <div className="text-sm text-error">{link.metadataError}</div>
                  )}

                  {link.metadata && (
                    <div className="space-y-2">
                      {/* Thumbnail */}
                      {link.metadata.thumbnailUrl && (
                        <div className="flex items-start gap-2">
                          <img
                            src={link.metadata.thumbnailUrl}
                            alt={link.metadata.title}
                            className="w-24 h-24 object-cover rounded border border-border"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-foreground">
                              {link.metadata.title}
                            </div>
                            {link.metadata.author && (
                              <div className="text-xs text-foreground-muted flex items-center gap-1 mt-1">
                                <User className="h-3 w-3" />
                                {link.metadata.author}
                              </div>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => useThumbnail(link.metadata!.thumbnailUrl!)}
                              className="mt-2"
                              leftIcon={<ImageIcon className="h-3 w-3" />}
                            >
                              Use Thumbnail
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Title */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="text-xs font-medium text-foreground-muted mb-1">
                            Title
                          </div>
                          <div className="text-sm text-foreground">{link.metadata.title}</div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => useTitle(link.metadata!.title)}
                          leftIcon={<Check className="h-3 w-3" />}
                        >
                          Use
                        </Button>
                      </div>

                      {/* Description */}
                      {link.metadata.description && (
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="text-xs font-medium text-foreground-muted mb-1">
                              Description
                            </div>
                            <div className="text-sm text-foreground line-clamp-3">
                              {link.metadata.description}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => useDescription(link.metadata!.description!)}
                            leftIcon={<Check className="h-3 w-3" />}
                          >
                            Use
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Accordion>

      {/* Files Section */}
      <Accordion
        title="Files"
        defaultOpen={true}
        badge={pendingFiles.length || undefined}
      >
        <div className="space-y-2">
          {/* Multi-File Drop Zone */}
          <MultiFileUpload
            onFilesSelect={handleMultipleFilesSelect}
            disabled={isSubmitting}
          />

          {/* Pending Files List */}
          {pendingFiles.length > 0 && (
            <div className="space-y-2">
              {pendingFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-start justify-between gap-3 p-3 bg-background-subtle rounded-lg border border-border"
                >
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <File className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-foreground truncate">
                        {file.file.name}
                      </div>
                      <div className="text-xs text-foreground-muted">
                        {formatFileSize(file.file.size)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(file.id)}
                    className="text-foreground-muted hover:text-error"
                    disabled={isSubmitting}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Accordion>

      {/* Notes Section */}
      <Accordion title="Notes" defaultOpen={false}>
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isSubmitting}
          rows={3}
          placeholder="Optional notes about this model"
        />
      </Accordion>

      {/* Submit Button */}
      <div className="pt-4 border-t border-border">
        <Button
          type="submit"
          disabled={isSubmitting || (!pendingFiles.length && !pendingLinks.length)}
          loading={isSubmitting}
          className="w-full"
        >
          Create Model
        </Button>
        <p className="text-xs text-center text-foreground-muted mt-2">
          {pendingFiles.length > 0 && `${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''}`}
          {pendingFiles.length > 0 && pendingLinks.length > 0 && ' and '}
          {pendingLinks.length > 0 && `${pendingLinks.length} link${pendingLinks.length !== 1 ? 's' : ''}`}
          {(pendingFiles.length > 0 || pendingLinks.length > 0) && ' will be added'}
        </p>
      </div>
    </form>
  );
}
